import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip, // Added Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PeopleIcon from '@mui/icons-material/People';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'; // Import new icon
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'; // Added InfoOutlinedIcon
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import axios from 'axios';
import { CONFIG } from '../../../../../config';
import ShiftUserList from '../components/auto-assign/ShiftUserList';
import AutoAssignAlgorithms from '../components/auto-assign/AutoAssignAlgorithms';
import SimpleAutoAssignAlgorithm from '../components/auto-assign/SimpleAutoAssignAlgorithm'; // Import the new component

const API_HOST = CONFIG.modules['accounts'].host;
const API_HOST_CRM = CONFIG.modules['crm'].host;

// Check if current time is between shift start and end
const isTimeBetween = (currentTime, startTime, endTime) => {
  // convert the currentTime to seconds 
  const currentTimeInSeconds = currentTime.hour() * 3600 + currentTime.minute() * 60 + currentTime.second();
  const startTimeInSeconds = dayjs(startTime).hour() * 3600 + dayjs(startTime).minute() * 60 + dayjs(startTime).second();
  const endTimeInSeconds = dayjs(endTime).hour() * 3600 + dayjs(endTime).minute() * 60 + dayjs(endTime).second();

  return currentTimeInSeconds >= startTimeInSeconds && currentTimeInSeconds <= endTimeInSeconds;
}


/**
 * 
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data object.
 * @param {boolean} props.open - The open state.
 * @param {Function} props.handleClose - The handle close function.
 * @param {Object} props.staticSelectedRows - The static selected rows.
 * @param {Function} props.handleRemoveStaticSelection - The handle remove static selection function.
 * @param {Object} props.filter - The filter object.
 * @returns {JSX.Element}
 */
const AutoAssignDialogFull = ({
  data,
  open,
  handleClose,
  staticSelectedRows,
  handleRemoveStaticSelection,
  filter
}) => {

  const currentTime = dayjs();

  const [shiftsForCurrentDay, setShiftsForCurrentDay] = useState([]);
  const [shiftsForCurrentDayLoading, setShiftsForCurrentDayLoading] = useState(true);
  const [selectedShift, setSelectedShift] = useState(null);
  const [usersInShift, setUsersInShift] = useState([]);
  const [usersInShiftLoading, setUsersInShiftLoading] = useState(false);
  const [ECDCampaign, setECDCampaign] = useState(null);
  const [selectedView, setSelectedView] = useState('users'); // 'users', 'algorithms', or 'simpleAuthAssign'

  const [autoAssigning, setAutoAssigning] = useState(false);
  const [autoAssigningLoading, setAutoAssigningLoading] = useState(false);

  // Current day use memo
  const currentDay = useMemo(() => {
    return {
      formatted: dayjs().format('dddd, MMMM D, YYYY'),
      dayjs: dayjs(),
      dayjsTime: dayjs().format('HH:mm')
    }
  }, []);

  // Categorize shifts into ongoing and completed
  const categorizedShifts = useMemo(() => {
    if (!shiftsForCurrentDay || shiftsForCurrentDay.length === 0) {
      return { ongoing: [], completed: [] };
    }

    const ongoing = [];
    const completed = [];

    shiftsForCurrentDay.forEach(shift => {
      if (isTimeBetween(currentTime, shift.start, shift.end)) {
        ongoing.push(shift);
      } else if (currentTime.isAfter(dayjs(shift.end))) {
        completed.push(shift);
      }
      // Shifts that haven't started yet are implicitly ignored
    });

    return { ongoing, completed };
  }, [shiftsForCurrentDay, currentTime]);

  const {
    Shifts,
    Campaigns,
    Languages
  } = useSelector((state) => state.user || {});

  // Initialize component data when opened
  useEffect(() => {
    if (open && Campaigns && Campaigns.length > 0) {
      // Find ECD campaign
      const ecd = Campaigns.find(c => c.name === 'ECD');
      if (ecd) {
        setECDCampaign(ecd);
        getShiftsForCurrentDay(ecd._id);
      }
      // Get shifts for current day

    }
  }, [open, Campaigns]);

  useEffect(() => {
    getShiftsForCurrentDay();
  }, [ECDCampaign]);

  // Api Method to get the shifts for the current day
  const getShiftsForCurrentDay = async (campaignId) => {
    try {
      setShiftsForCurrentDayLoading(true);
      const response = await axios.get(API_HOST + "/shifts-for-current-day", {
        params: {
          campaign: campaignId || ECDCampaign._id
        }
      });

      setShiftsForCurrentDay(response.data.shifts);
      setShiftsForCurrentDayLoading(false);
    } catch (e) {
      console.log(e);
      setShiftsForCurrentDayLoading(false);
    }
  }

  // Side effect that runs when the selected shift changes
  useEffect(() => {
    if (selectedShift) {
      getUsersFromShifts();
    }
  }, [selectedShift]);

  // Api Method to get the users from the shifts
  const getUsersFromShifts = async () => {
    try {
      setUsersInShiftLoading(true);

      // Selected Shape is a map type object
      const selectedShiftId = Object.keys(selectedShift)[0];
      const response = await axios.get(API_HOST + "/users-from-shifts", {
        params: {
          shift: selectedShiftId,
          schedule: selectedShift[selectedShiftId].schedule,
          campaign: ECDCampaign._id
        }
      });

      console.log(response.data.usersInShift, "Users in shift");

      // Check if the users in shift exists
      if (response.data.usersInShift) {
        // users in shift is an array that contains user property 
        // we need to get the users from the users array

        // check the length
        if (response.data.usersInShift.length > 0) {
          const modifiedUsersInShift = response.data.usersInShift.map((shiftWithUser) => {

            return shiftWithUser
          });

          setUsersInShift(modifiedUsersInShift);
        } else {
          setUsersInShift([]);
        }
      }

    } catch (e) {
      console.log(e);
    } finally {
      setUsersInShiftLoading(false);
    }
  }

  // Function to reload shift details (specifically user allocation)
  const reloadShiftDetails = () => {
    if (selectedShift) {
      getUsersFromShifts(); // Re-fetch user data for the current shift
    }
  };

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setSelectedView(newView);
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      aria-labelledby="auto-assign-dialog-title"
    >
      <DialogTitle id="auto-assign-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Tooltip
            title={
              <React.Fragment>
                <Typography color="inherit" variant="subtitle2">Auto Assign Information:</Typography>
                <ul>
                  <li>Considers cases currently in queue and cases not assigned today.</li>
                  <li>Matches cases and agents based on language and cluster.</li>
                  <li>If language/cluster mismatches occur, select a shift with agents matching the required language and clusters.</li>
                </ul>
              </React.Fragment>
            }
            arrow
          >
            <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'help' }}>
              <Typography variant="h6" sx={{ mr: 0.5 }}>Auto Assign</Typography>
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Box>
          </Tooltip>
          <IconButton
            disabled={autoAssigning}
            edge="end" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="shifts-content"
            id="shifts-header"
          >
            <Typography variant="h6">
              {selectedShift ? (
                <>
                  Selected: {selectedShift[Object.keys(selectedShift)[0]].name}
                  <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.5, mr: 0.5 }}>
                    ({dayjs(selectedShift[Object.keys(selectedShift)[0]].start).format('HH:mm')} - {dayjs(selectedShift[Object.keys(selectedShift)[0]].end).format('HH:mm')})
                  </Typography>
                  | Current: {currentDay.dayjsTime}
                </>
              ) : (
                <>
                  Shifts | Current: {currentDay.dayjsTime}
                </>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(() => {
              if (shiftsForCurrentDayLoading) {
                return (
                  <Box display="flex" justifyContent="center" my={4}>
                    <CircularProgress />
                  </Box>
                );
              }

              const { ongoing, completed } = categorizedShifts; // Use the categorized shifts

              if (ongoing.length === 0 && completed.length === 0) {
                return (
                  <Typography variant="body1" color="text.secondary" align="center" my={4}>
                    No shifts available or completed for today
                  </Typography>
                );
              }

              // Helper function to render a list of shifts
              const renderShiftList = (shifts, title, isOngoingList) => (
                shifts.length > 0 && (
                  <Box mb={3}> {/* Add margin between categories */}
                    <Typography variant="subtitle1" gutterBottom>{title}</Typography>
                    <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap' }}>
                      <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'nowrap' }}>
                        {shifts.map((shift) => {
                          // Determine if the shift card should be active/clickable
                          // Only ongoing shifts should be clickable
                          const isShiftActiveForSelection = isOngoingList;

                          return (
                            <Grid item key={shift._id} sx={{ minWidth: 250 }}>
                              <Card
                                variant="outlined"
                                sx={{
                                  height: 'auto',
                                  cursor: isShiftActiveForSelection ? 'pointer' : 'default',
                                  bgcolor: selectedShift && Object.keys(selectedShift)[0] === shift._id ? 'primary.light' : 'background.paper',
                                  p: { xs: 0.5, sm: 1 },
                                  opacity: isShiftActiveForSelection ? 1 : 0.6, // Dim completed shifts
                                }}
                              >
                                <CardActionArea
                                  sx={{ height: '100%' }}
                                  disabled={!isShiftActiveForSelection} // Disable completed shifts
                                  onClick={() => isShiftActiveForSelection && setSelectedShift({ [shift._id]: shift })}
                                >
                                  <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
                                    <Typography variant="h6" component="div" sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
                                      {shift.name}
                                      {/* Keep the (Full) indicator logic */}
                                      {shift.number && shift.allocated >= shift.number && (
                                        <Box component="span" sx={{ ml: 1, color: 'error.main', fontSize: '0.8em', verticalAlign: 'middle' }}>
                                          (Full)
                                        </Box>
                                      )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                      Time: {dayjs(shift.start).format('HH:mm')} - {dayjs(shift.end).format('HH:mm')}
                                    </Typography>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  </Box>
                )
              );

              return (
                <>
                  {renderShiftList(ongoing, 'Ongoing Shifts', true)}
                  {renderShiftList(completed, 'Completed Shifts', false)}
                </>
              );
            })()}
          </AccordionDetails>
        </Accordion>

        {selectedShift && (
          <Box mt={2}> {/* Reduced margin top slightly */}
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="selected-shift-content"
                id="selected-shift-header"
              >
                <Typography variant="h6">
                  Selected Shift: {selectedShift[Object.keys(selectedShift)[0]].name}
                  <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 1 }}>
                    ({dayjs(selectedShift[Object.keys(selectedShift)[0]].start).format('HH:mm')} - {dayjs(selectedShift[Object.keys(selectedShift)[0]].end).format('HH:mm')})
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box> {/* Added a Box wrapper for inner content */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <ToggleButtonGroup
                      value={selectedView}
                      exclusive
                      onChange={handleViewChange}
                      aria-label="View selection"
                      sx={{
                        // Default styles for larger screens
                        '& .MuiToggleButton-root': {
                          px: 2, // Default padding
                          fontSize: '0.6rem',
                        },
                        // Styles for smaller screens (e.g., 'sm' breakpoint and down)

                      }}
                    >
                      <ToggleButton value="users" aria-label="Show users"
                        sx={{
                          sm: {
                            p: 1,
                            fontSize: '0.6rem',
                            '& .MuiSvgIcon-root': {
                              mr: 0.5, // Slightly reduce margin for icon
                              fontSize: '1.1rem', // Make icon slightly smaller
                            },
                          }
                        }}>
                        <PeopleIcon sx={{ mr: 1 }} />
                        Shift Agents
                      </ToggleButton>
                      {/* <ToggleButton value="algorithms" aria-label="Show algorithms">
                        <AutoAwesomeIcon sx={{ mr: 1 }} />
                        Auto-Assign Algorithms
                      </ToggleButton> */}
                      <ToggleButton value="simpleAutoAssign" aria-label="Show Simple Auto Assign Algorithm">
                        <AssignmentIndIcon sx={{ mr: 1 }} />
                        Simple Auto Assign Algorithm
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {selectedView === 'users' && (
                    <ShiftUserList
                      usersInShift={usersInShift}
                      usersInShiftLoading={usersInShiftLoading}
                      selectedShift={selectedShift}
                      Languages={Languages}
                    />
                  )}

                  {selectedView === 'algorithms' && (
                    <AutoAssignAlgorithms />
                  )}

                  {selectedView === 'simpleAutoAssign' && (
                    <SimpleAutoAssignAlgorithm
                      shift={selectedShift[Object.keys(selectedShift)[0]]}
                      usersInShift={usersInShift}
                      filter={filter}
                      autoAssigning={autoAssigning}
                      setAutoAssigning={setAutoAssigning}
                      onAssignmentSuccess={reloadShiftDetails} // Pass the reload function
                    />
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        )}

        {/* Debug data */}
        <Box mt={4} sx={{ display: 'none' }}>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={autoAssigning}
          onClick={handleClose} color="primary">
          Close
        </Button>
        {/* <Button variant="contained" color="primary">
          Save
        </Button> */}
      </DialogActions>
    </Dialog>
  );
};

export default AutoAssignDialogFull;
